/**
 * Text matching and normalization utilities for citation highlighting
 * Implements the matching heuristic for finding citation text in documents
 */

import { logger } from "@/telemetry";

/**
 * Normalize text for matching by removing formatting and standardizing characters
 * @param text - Raw text to normalize
 * @returns Normalized text
 */
export function normalizeText(text: string): string {
  return (
    text
      .toLowerCase()
      // Strip HTML tags first so subsequent rules see clean text.
      // Azure Document Intelligence's prebuilt-layout emits markdown with
      // embedded HTML (<table>, <tr>, <td>, <th>, <figure>, etc.) — those
      // tags do not appear in rendered PDF/DOCX text, so they must be
      // removed before matching. Replace with a space to preserve word
      // boundaries (e.g. "</td><td>" -> " ").
      .replace(/<[^>]+>/g, " ")
      // Decode common HTML entities that DI markdown contains.
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      // Collapse all whitespace (spaces, tabs, newlines) to single spaces
      .replace(/\s+/g, " ")
      // Normalize quotes
      .replace(/['']/g, "'")
      .replace(/[""]/g, '"')
      // Normalize dashes
      .replace(/[–—]/g, "-")
      // Remove zero-width spaces and other invisible characters
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      // Dehyphenate line breaks (e.g., "com-\npany" -> "company")
      .replace(/(\w)-\s*\n(\w)/g, "$1$2")
      // Strip markdown headers (e.g., "## Title" -> "Title")
      .replace(/^#+\s+/gm, "")
      // Strip markdown bold/italic markers (e.g., "**text**" -> "text", "*text*" -> "text")
      .replace(/\*+/g, "")
      // Strip markdown underline emphasis (e.g., "__text__" -> "text")
      .replace(/__/g, "")
      // Strip bullet points
      .replace(/^[-•]\s+/gm, "")
      // Strip markdown table delimiters and separator rows (e.g., "| cell | cell |", "| --- |")
      .replace(/\|/g, " ")
      .replace(/\s*-{3,}\s*/g, " ")
      // Strip leading list/section number prefixes at the start of a line.
      // Covers "1. Heading", "1) Heading", bare-number "1 Heading", and
      // decimal headings "9.1 Heading" / "9.1.2 Heading". The bare-number
      // form arises when a PDF lays the section number out as a separate
      // table-cell from the title and pdfjs joins items with " ". The 4+
      // char lookahead avoids eating inline numerics like "1 a" or "9 of".
      // Applied symmetrically to citation and document text so "1 Scope ..."
      // and "Scope ..." normalise identically — see matching-failure
      // analysis for the NG_ET_BP_048 / "Scope" case.
      .replace(/^\d+(?:\.\d+)*[.)]?\s+(?=\w{4,})/gm, "")
      .trim()
  );
}

/**
 * Extract salient tokens (meaningful words) from text
 * Filters out short words and common stop words
 * @param text - Text to extract tokens from
 * @param minLength - Minimum token length (default: 4)
 * @returns Array of salient tokens
 */
export function extractSalientTokens(text: string, minLength: number = 4): string[] {
  const stopWords = new Set([
    "the",
    "and",
    "for",
    "that",
    "this",
    "with",
    "from",
    "have",
    "been",
    "were",
    "their",
    "there",
    "where",
    "which",
    "what",
    "when",
    "will",
    "would",
    "could",
    "should",
  ]);

  return text
    .split(/\s+/)
    .filter((token) => token.length >= minLength && !stopWords.has(token.toLowerCase()))
    .slice(0, 8); // Limit to first 8 salient tokens
}

/**
 * Match result containing position and confidence
 */
export interface MatchResult {
  /** Starting position in the document text */
  position: number;

  /** Length of the matched text */
  length: number;

  /** Confidence score (0-1) */
  confidence: number;

  /** Matched text from the document */
  matchedText: string;

  /** Match method used: 'exact' | 'head' | 'token-overlap' */
  method: "exact" | "head" | "token-overlap";
}

/**
 * Find citation text in document text using multiple strategies
 * @param documentText - Full text from the document
 * @param citationText - Citation text to find
 * @returns Match result or null if no match found
 */
export function findTextMatch(documentText: string, citationText: string): MatchResult | null {
  // Normalize both texts
  const normDoc = normalizeText(documentText);
  const normCite = normalizeText(citationText);

  if (!normDoc || !normCite) {
    return null;
  }

  // Strategy 1: Try exact match on full citation text
  let position = normDoc.indexOf(normCite);

  if (position !== -1) {
    return {
      position,
      length: normCite.length,
      confidence: 1.0,
      matchedText: normCite,
      method: "exact",
    };
  }

  // Strategy 2: Try exact match on head (first ~100 chars)
  const headLength = Math.min(100, normCite.length);
  const head = normCite.substring(0, headLength);

  position = normDoc.indexOf(head);

  if (position !== -1) {
    return {
      position,
      length: head.length,
      confidence: 0.9,
      matchedText: head,
      method: "head",
    };
  }

  // Strategy 2.5: Try with first ~50 chars (more specific)
  const shorterHeadLength = Math.min(50, normCite.length);
  const shorterHead = normCite.substring(0, shorterHeadLength);

  position = normDoc.indexOf(shorterHead);

  if (position !== -1) {
    return {
      position,
      length: shorterHead.length,
      confidence: 0.85,
      matchedText: shorterHead,
      method: "head",
    };
  }

  // Strategy 3: Token-overlap window with salient tokens
  const salientTokens = extractSalientTokens(normCite);

  if (salientTokens.length === 0) {
    return null;
  }

  // Extract any numbers/section markers from citation (e.g., "9.1", "9.2", "clause 9")
  const numberPattern = /\b\d+(?:\.\d+)*\b/g;
  const citationNumbers = normCite.match(numberPattern) || [];

  // Check if citation looks like a section header (e.g., "9. transfers" at the start)
  // Section headers are distinctive: number + short title at the beginning
  const sectionHeaderPattern = /^(\d+\.?\d*)\s+(\w+)/;
  const headerMatch = normCite.match(sectionHeaderPattern);
  const isLikelySectionHeader = headerMatch !== null;

  if (isLikelySectionHeader) {
    // findTextMatch is called per-page from PDFViewer; demote to debug to
    // avoid hundreds of telemetry events per citation on large PDFs.
    logger.debug(`🔍 Citation appears to be section header: "${headerMatch![0]}"`);
  }

  const docWords = normDoc.split(/\s+/);
  const windowSize = 20; // Number of words to check in each window
  // Proportional threshold: require at least 60% of tokens (min 2) instead of fixed 5
  // This helps short citation texts (e.g., 3 salient tokens → need 2, not all 3)
  const minTokenMatches = Math.max(2, Math.ceil(salientTokens.length * 0.6));
  let bestMatch: MatchResult | null = null;
  let bestScore = 0;

  for (let i = 0; i < docWords.length - windowSize; i++) {
    const window = docWords.slice(i, i + windowSize).join(" ");

    // Count how many salient tokens appear in this window
    const matchedTokens = salientTokens.filter((token) => window.includes(token));

    if (matchedTokens.length >= minTokenMatches) {
      let score = matchedTokens.length / salientTokens.length;

      // Boost score if window contains the same numbers (section markers are very distinctive)
      if (citationNumbers.length > 0) {
        const windowNumbers = window.match(numberPattern) || [];
        const numberMatches = citationNumbers.filter((num) =>
          windowNumbers.some((winNum) => winNum === num),
        ).length;

        if (numberMatches > 0) {
          // Strong boost for matching section numbers
          score += numberMatches * 0.3;
        }
      }

      // EXTRA BOOST: If citation is a section header, heavily favor windows that START with that section number
      if (isLikelySectionHeader && headerMatch) {
        const sectionNum = headerMatch[1]; // e.g., "9" or "9.1"
        const sectionTitle = headerMatch[2]; // e.g., "transfers"

        // Escape regex metacharacters in user-derived values to avoid
        // throwing on (e.g.) citation titles containing "(", "+", "?",
        // and to prevent decimal numbers like "9.1" matching "9X1".
        const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const windowStartPattern = new RegExp(
          `^${escapeRegex(sectionNum)}\\s+${escapeRegex(sectionTitle)}`,
          "i",
        );

        if (windowStartPattern.test(window)) {
          // HUGE boost for matching the actual section header (not just a reference to it)
          score += 1.0;
          logger.debug(`🎯 Found section header start: "${window.substring(0, 30)}..."`);
        }
      }

      if (score > bestScore) {
        // Find the position of this window in the original normalized document
        let windowPosition = normDoc.indexOf(window);

        // Try to find where the first salient token appears in the window
        // to get a more precise starting position
        if (windowPosition !== -1 && matchedTokens.length > 0) {
          const firstToken = matchedTokens[0];
          const tokenPositionInWindow = window.indexOf(firstToken);

          if (tokenPositionInWindow !== -1) {
            // Adjust position to start at the first matched token instead of window start
            windowPosition += tokenPositionInWindow;
          }
        }

        if (windowPosition !== -1) {
          bestScore = score;
          bestMatch = {
            position: windowPosition,
            length: window.length,
            confidence: Math.min(score * 0.8, 0.95), // Multiply by 0.8 to indicate fuzzy match, cap at 95%
            matchedText: window,
            method: "token-overlap",
          };
        }
      }
    }
  }

  if (bestMatch) {
    return bestMatch;
  }

  // Strategy 4: Character n-gram sliding window (handles text format mismatches
  // between plain text indexing and PDF rendering where words get split/joined differently)
  const citeWords = normCite.split(/\s+/);
  const ngramSize = 3; // Use trigrams of words

  if (citeWords.length >= ngramSize) {
    // Build n-grams from citation
    const citeNgrams = new Set<string>();

    for (let i = 0; i <= citeWords.length - ngramSize; i++) {
      citeNgrams.add(citeWords.slice(i, i + ngramSize).join(" "));
    }

    const scanWindowSize = Math.min(40, docWords.length); // Wider window for fuzzy scan
    let bestFuzzyScore = 0;
    let bestFuzzyMatch: MatchResult | null = null;
    // Require at least 30% of citation n-grams to match
    const minNgramRatio = 0.3;

    for (let i = 0; i <= docWords.length - scanWindowSize; i++) {
      const windowWords = docWords.slice(i, i + scanWindowSize);
      let matchCount = 0;

      for (let j = 0; j <= windowWords.length - ngramSize; j++) {
        const ngram = windowWords.slice(j, j + ngramSize).join(" ");

        if (citeNgrams.has(ngram)) {
          matchCount++;
        }
      }

      const ratio = matchCount / citeNgrams.size;

      if (ratio >= minNgramRatio && ratio > bestFuzzyScore) {
        const windowText = windowWords.join(" ");
        const windowPosition = normDoc.indexOf(windowText);

        if (windowPosition !== -1) {
          bestFuzzyScore = ratio;
          bestFuzzyMatch = {
            position: windowPosition,
            length: windowText.length,
            confidence: Math.min(ratio * 0.7, 0.8), // Lower confidence for fuzzy match
            matchedText: windowText,
            method: "token-overlap", // Reuse type since MatchResult has limited method options
          };
        }
      }
    }

    if (bestFuzzyMatch) {
      return bestFuzzyMatch;
    }
  }

  return null;
}

/**
 * Split text into words while preserving their original positions
 * @param text - Text to split
 * @returns Array of words with their positions
 */
export interface WordWithPosition {
  word: string;
  position: number;
}

export function splitTextWithPositions(text: string): WordWithPosition[] {
  const words: WordWithPosition[] = [];
  const regex = /\S+/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    words.push({
      word: match[0],
      position: match.index,
    });
  }

  return words;
}

/**
 * Calculate similarity score between two texts using Jaccard similarity
 * @param text1 - First text
 * @param text2 - Second text
 * @returns Similarity score (0-1)
 */
export function calculateSimilarity(text1: string, text2: string): number {
  const tokens1 = new Set(normalizeText(text1).split(/\s+/));
  const tokens2 = new Set(normalizeText(text2).split(/\s+/));

  const intersection = new Set([...tokens1].filter((x) => tokens2.has(x)));
  const union = new Set([...tokens1, ...tokens2]);

  if (union.size === 0) return 0;

  return intersection.size / union.size;
}
